import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { clearToken, getMe, getStoredUser, getToken, login as apiLogin, setStoredUser, setToken, signup as apiSignup } from '../services/api';
import { AuthResponse, User } from '../types/api';

type AuthContextValue = {
  token: string | null;
  user: User | null;
  isAuthenticated: boolean;
  ready: boolean;
  login: (email: string, password: string) => Promise<AuthResponse>;
  signup: (name: string, email: string, password: string) => Promise<AuthResponse>;
  completeLogin: (token: string, user: User) => void;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<User | null>(() => getStoredUser());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    if (!token) {
      setReady(true);
      return () => {
        active = false;
      };
    }

    getMe()
      .then((profile) => {
        if (!active) return;
        setUser(profile);
        setStoredUser(profile);
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setReady(true);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const completeLogin = useCallback((nextToken: string, nextUser: User) => {
    setToken(nextToken);
    setStoredUser(nextUser);
    setTokenState(nextToken);
    setUser(nextUser);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const auth = await apiLogin(email, password);
    setTokenState(auth.token);
    setUser(auth.user);
    return auth;
  }, []);

  const signup = useCallback(async (name: string, email: string, password: string) => {
    const auth = await apiSignup(name, email, password);
    setTokenState(auth.token);
    setUser(auth.user);
    return auth;
  }, []);

  const logout = useCallback(() => {
    clearToken();
    setTokenState(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated: Boolean(token),
      ready,
      login,
      signup,
      completeLogin,
      logout,
    }),
    [token, user, ready, login, signup, completeLogin, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
};
